import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Offcanvas, OffcanvasBody, Spinner } from 'reactstrap'
import PerfectScrollbar from 'react-perfect-scrollbar'
import 'react-perfect-scrollbar/dist/css/styles.css'
import { bookmarkApplciation, fetchApplication } from 'slices/campaigns/applications/thunk';
import { fetchInfluencerSocialAccount } from 'slices/influencer/thunk';
import { AppDispatch, RootState } from 'store';
import "./style.scss";
import SocialAccountProfile from './social-account/SocialAccountProfile';
import SocialAccountStats from './social-account/SocialAccountStats';
import { genders } from 'constants/campaign';
import AIEvaluation from './AIEvaluation';
import ApplicationViewMedia from './social-account/ApplicationViewMedia';
import SocialAccountInsights from './social-account/SocialAccountInsights';
import { usePrivileges } from 'hooks/useAuth';
import { PRIVILEGE } from 'constants/privilege';
import { toast } from 'react-toastify';
import { getErrorMessage } from 'utils/helpers';
import { processApplication as processApplicationAPI, revertApplicationRejection as revertApplicationRejectionAPI } from 'services/api/campaign/application';
import Swal from 'sweetalert2';
import { updateApplicationStatus } from 'slices/campaigns/applications/reducer';
import { updateRemainingBudget } from 'slices/campaigns/reducer';
import { toggleApplicationView } from 'slices/ui/reducer';
import { campaignIconSVG } from 'pages/Campaign/Campaign';
import CloseButton from 'Components/Common/CloseButton';
import ScreeningQuestionAnswers from './ScreeningQuestionAnswers';

interface ActionConfirmation {
    action: 'approve' | 'reject';
    onConfirmation: () => void;
}

const ApplicationView = ({ isOpen, toggleOffcanvas, applicationId, campaignId, socialAccountId }: { isOpen: boolean, toggleOffcanvas: () => void, applicationId?: string, campaignId?: string, socialAccountId: string }) => {

    const dispatch: AppDispatch = useDispatch();
    const privileges = usePrivileges();
    const [actionConfirmation, setActionConfirmation] = useState<null | ActionConfirmation>(null);
    const campaign = useSelector((state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign);


    const applicationData = useSelector((state: RootState) => state.campaignApplications.campaigns[campaignId!]?.applications?.[applicationId!]?.application) ?? null;
    const applicationStatus = applicationData?.status;
    const isShortlisted = applicationData?.bookmarked === 'y';
    const socialAccount = useSelector((state: RootState) => state.influencers.social_accounts[socialAccountId!]?.social_account);
    const nicheList = useSelector((state: RootState) => state.auth.master?.niche_list ?? {});
    const specialityNiches = useSelector((state: RootState) => state.auth.master?.speciality_niches) ?? [];
    const connectionRequired = Boolean(
        useSelector((state: RootState) => state.campaigns.items[campaignId!]?.data?.campaign?.connection_required)
    );
    const platform = socialAccount?.platform;
    const influencer = applicationData?.influencer;
    const [bookmarking, setBookmarking] = useState(false);
    const fetchApplicationData = useCallback(async (campaignId: string, applicationId: string, force: boolean = false) => {
        dispatch(fetchApplication({ campaignId, applicationId, force }));
    }, [dispatch])

    useEffect(() => {
        if (isOpen && socialAccountId) {
            dispatch(fetchInfluencerSocialAccount({ socialAccountId }))
        }
    }, [isOpen, socialAccountId])

    useEffect(() => {
        if (isOpen && applicationId && campaignId) {
            fetchApplicationData(campaignId, applicationId);
        }
    }, [applicationId, campaignId, isOpen])

    const creatorGender = useMemo(() => {
        if (influencer?.gender_name) return influencer?.gender_name;
        if (platform === 'ig' && socialAccount?.ig?.gender) {
            return genders[socialAccount?.ig?.gender as keyof typeof genders];
        }
        return "";
    }, [platform, socialAccount, influencer])

    const creatorLocation = useMemo(() => {
        return influencer?.location ?? socialAccount?.location ?? "";
    }, [influencer, socialAccount])

    const creatorAge = useMemo(() => {
        if (influencer?.age) return influencer?.age.toString();
        if (platform === 'ig' && socialAccount?.ig?.age_bucket) {
            return socialAccount?.ig?.age_bucket ?? "";
        }
        return "";

    }, [influencer, socialAccount, platform])

    const followersCount = useMemo(() => {
        if (platform === 'ig') return socialAccount?.ig?.followers_count ?? 0;
        if (platform === 'yt') return socialAccount?.yt?.subscriber_count ?? 0;
        return 0;
    }, [socialAccount, platform])

    const specialityNichesTier2 = useMemo(() => {
        if (!Array.isArray(specialityNiches)) return [];
        const tier2 = [];
        for (const niche of specialityNiches) {
            if (Array.isArray(niche.children)) tier2.push(...niche.children);
        }
        return tier2;
    }, [specialityNiches]);

    const creatorNiches = useMemo(() => {
        if (campaign?.requirements?.speciality_niches && campaign.requirements.speciality_niches?.length > 0) {
            if (socialAccount?.speciality_niches_tier2 && socialAccount.speciality_niches_tier2.length > 0) {
                return socialAccount.speciality_niches_tier2
                    .map((niche) => (specialityNichesTier2 ?? []).find((n) => Number(n.id) === Number(niche))?.name ?? '')
                    .filter((label): label is string => Boolean(label));
            }
            if (socialAccount?.speciality_niches && socialAccount.speciality_niches.length > 0) {
                return socialAccount.speciality_niches
                    .map((niche) => (specialityNiches ?? []).find((n) => Number(n.id) === Number(niche))?.name ?? '')
                    .filter((label): label is string => Boolean(label));
            }
            return [];
        }
        if (socialAccount?.niches && socialAccount.niches.length > 0) {
            return socialAccount.niches
                .map((nicheId) => nicheList[nicheId])
                .filter((label): label is string => Boolean(label));
        }
        return [];
    }, [campaign, socialAccount, nicheList, specialityNiches, specialityNichesTier2])

    const evaluation = useMemo(() => {
        const aiEval = applicationData?.evaluation?.evaluation;
        if (!aiEval || applicationStatus === 'accepted' || (applicationData.bookmarked === 'y' && aiEval.final_verdict.recommendation === 'REJECT')) return null;
        return aiEval;
    }, [applicationData])

    const hasDemographicInsights = useMemo(() => {
        const demographic = platform === 'ig' ? socialAccount?.ig?.insights?.demographic : socialAccount?.yt?.insights?.demographic
        if (!demographic) return false

        const hasAny =
            Object.keys(demographic.gender ?? {}).length > 0 ||
            Object.keys(demographic.age ?? {}).length > 0 ||
            Object.keys(demographic.city ?? {}).length > 0 ||
            Object.keys(demographic.country ?? {}).length > 0

        return hasAny
    }, [socialAccount?.ig?.insights?.demographic])

    const shouldMergePanels = hasDemographicInsights && !evaluation

    const showSeparatePanelForDemographicInsights = hasDemographicInsights && !shouldMergePanels;

    const processApplication = useCallback(async (applicationId: string, action: 'accept' | 'reject', force: boolean = false) => {
        if (!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS) || !campaignId || !applicationId) {
            toast.error("You don't have permission to process application!");
            return;
        }
        try {
            dispatch(updateApplicationStatus({ campaignId, applicationId: applicationId, status: 'processing' }));
            const result = await processApplicationAPI(campaignId, applicationId, { action, force });
            if (result.status && result.application?.status) {
                dispatch(updateApplicationStatus({ campaignId, applicationId: applicationId, status: result.application.status }));
            }
            if (result.campaign?.remaining_budget) {
                dispatch(updateRemainingBudget({ campaignId, budget: result.campaign.remaining_budget }));
            }
            setActionConfirmation(null);
            dispatch(toggleApplicationView(null));
            toggleOffcanvas();
        } catch (error: any) {
            if (error?.response?.data?.error_code === "existing_ongoing_collabs") {
                const r = await Swal.fire({
                    title: error?.response?.data?.message ?? "This creator already has ongoing collaborations in other campaigns",
                    iconHtml: campaignIconSVG,
                    showCancelButton: true,
                    confirmButtonText: "Approve Anyway",
                    cancelButtonText: "Cancel",
                    reverseButtons: true
                });

                if (r.isConfirmed) {
                    return processApplication(applicationId, action, true);
                }
                dispatch(updateApplicationStatus({ campaignId, applicationId: applicationId, status: 'pending' }));
                return;
            }
            toast.error(getErrorMessage(error, "Failed to process application!"));
            dispatch(updateApplicationStatus({ campaignId, applicationId: applicationId, status: 'pending' }));
        }
    }, [campaignId, dispatch, privileges, toggleOffcanvas]);

    const revertRejection = useCallback(async () => {
        if (!applicationData?.uuid || !campaignId || !applicationId) return;

        if (!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)) {
            toast.error("You don't have permission to process application!");
            return;
        }

        const r = await Swal.fire({
            title: 'Are you sure you want to revert this rejected application?',
            iconHtml: campaignIconSVG,
            showCancelButton: true,
            confirmButtonText: 'Yes, Revert',
            cancelButtonText: 'No, Cancel',
            reverseButtons: true,
        });

        if (!r.isConfirmed) return;

        try {
            dispatch(updateApplicationStatus({ campaignId, applicationId: applicationData.uuid, status: 'processing' }));
            const result = await revertApplicationRejectionAPI(campaignId, applicationData.uuid);
            if (result.status && result.application?.status) {
                dispatch(updateApplicationStatus({ campaignId, applicationId: applicationData.uuid, status: result.application.status }));
            } else {
                dispatch(updateApplicationStatus({ campaignId, applicationId: applicationData.uuid, status: 'pending' }));
            }
            if (result.campaign?.remaining_budget) {
                dispatch(updateRemainingBudget({ campaignId, budget: result.campaign.remaining_budget }));
            }
            toast.success('Application reverted successfully.');
        } catch (error: unknown) {
            toast.error(getErrorMessage(error, 'Failed to revert application!'));
            dispatch(updateApplicationStatus({ campaignId, applicationId: applicationData.uuid, status: 'rejected' }));
        }
    }, [applicationData?.uuid, campaignId, dispatch, privileges]);

    const reject = useCallback(async () => {
        if (!applicationData?.uuid) return;
        await processApplication(applicationData.uuid, 'reject');
    }, [applicationData?.uuid, processApplication]);

    const bookmark = async (value: 'y' | 'n', force: boolean = false) => {
        if (bookmarking || !applicationId || !campaignId) return;
        const animateShortlist = value === 'y' && !isShortlisted;
        try {
            setBookmarking(true);
            const result = await dispatch(
                bookmarkApplciation({
                    campaignId,
                    applicationId: applicationId,
                    bookmark: value,
                    animateShortlist,
                    force,
                })
            );
            if (bookmarkApplciation.rejected.match(result)) {
                const errorCode = result.payload?.errorCode ?? 'unknown_error';
                const message = result.payload?.message ?? 'Failed to shortlist this application';
                if (errorCode && ['existing_ongoing_collabs', 'application_restricted'].includes(errorCode)) {
                    const r = await Swal.fire({
                        title: message,
                        iconHtml: campaignIconSVG,
                        showCancelButton: true,
                        confirmButtonText: "Shortlist Anyway",
                        cancelButtonText: "Cancel",
                        reverseButtons: true
                    });
                    if (r.isConfirmed) {
                        return bookmark(value, true);
                    }
                } else {
                    toast.error(message);
                }
            }
            if (animateShortlist && bookmarkApplciation.fulfilled.match(result)) {
                toast.success('Moved to Shortlisted', { autoClose: 2200 });
            }
        } catch (error: any) {
            console.log('error');
        } finally {
            setBookmarking(false);
        }
    };


    return (
        <Offcanvas
            isOpen={isOpen}
            toggle={toggleOffcanvas}
            direction='end'
            id="application-view-offcanvas"
            className={`application-view-offcanvas ${!showSeparatePanelForDemographicInsights ? 'application-view-offcanvas--merged' : ''}`}
        >
            <CloseButton onClose={toggleOffcanvas} />
            <OffcanvasBody className="application-view-body vh-100 overflow-hidden">
                <div className="application-view"> {/* Panel 1 */}
                    <div className={`application-view__panel application-view__panel--with-footer`}>
                        <div className="application-view__panel-scroll">
                            <PerfectScrollbar options={{ suppressScrollX: true }}>
                                <div className="application-view__panel-content">
                                    {socialAccount && (
                                        <>
                                            <SocialAccountProfile
                                                socialAccount={socialAccount}
                                                name={influencer?.name ?? (socialAccount?.name ?? '')}
                                                handle={socialAccount.handle!}
                                                followersCount={followersCount}
                                                gender={creatorGender}
                                                location={creatorLocation}
                                                age={creatorAge}
                                                niches={creatorNiches}
                                            />
                                            <SocialAccountStats
                                                socialAccount={socialAccount}
                                                connectionRequired={connectionRequired}
                                            />
                                        </>
                                    )}
                                    {
                                        applicationData?.answers && applicationData?.answers?.length > 0 &&
                                        <div className='mt-3'>
                                            <ScreeningQuestionAnswers answers={applicationData.answers} />
                                        </div>
                                    }
                                    {evaluation && (
                                        <AIEvaluation evaluation={evaluation} />
                                    )}
                                    {shouldMergePanels && socialAccount ? (
                                        <div className="application-view__merged-panel">
                                            <SocialAccountInsights socialAccount={socialAccount} />
                                        </div>
                                    ) : null}
                                </div>
                            </PerfectScrollbar>
                        </div>
                        {
                            applicationId && campaignId && (
                                <div className="application-view__panel-footer">
                                    <div className="application-view__panel-footer-inner">
                                        {applicationStatus === 'pending' ? (
                                            actionConfirmation ? (
                                                <div
                                                    className={`application-view__footer-banner ${actionConfirmation.action === 'reject' ? 'application-view__footer-banner--danger' : 'application-view__footer-banner--success'}`}
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <span>Are you sure, You want to {actionConfirmation.action} ?</span>
                                                    <div className="application-view__footer-banner-actions">
                                                        <button className="application-view__footer-banner-btn" onClick={actionConfirmation.onConfirmation}>
                                                            <i className='bx bx-check fs-4 text-success' />
                                                        </button>
                                                        <button className="application-view__footer-banner-btn" onClick={() => setActionConfirmation(null)}>
                                                            <i className='bx bx-x fs-4 text-danger' />
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        className={`application-view__footer-btn application-view__footer-shortlist-btn ${isShortlisted ? 'application-view__footer-shortlist-btn--shortlisted' : 'application-view__footer-btn--primary'}`}
                                                        disabled={bookmarking}
                                                        onClick={() => bookmark(isShortlisted ? 'n' : 'y')}
                                                    >
                                                        {bookmarking ? (
                                                            <Spinner size="sm" color={isShortlisted ? 'primary' : 'light'} />
                                                        ) : isShortlisted ? (
                                                            <>
                                                                <span className="application-view__footer-shortlist-btn__label application-view__footer-shortlist-btn__label--default">
                                                                    SHORTLISTED
                                                                </span>
                                                                <span className="application-view__footer-shortlist-btn__label application-view__footer-shortlist-btn__label--hover">
                                                                    REMOVE FROM SHORTLIST
                                                                </span>
                                                            </>
                                                        ) : (
                                                            '+ SHORTLIST'
                                                        )}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)}
                                                        className="application-view__footer-btn application-view__footer-btn--outline"
                                                        onClick={() => setActionConfirmation({ action: 'reject', onConfirmation: reject })}
                                                    >
                                                        REJECT
                                                    </button>
                                                </>
                                            )
                                        ) : null}

                                        {applicationStatus === 'processing' ? (
                                            <div className="application-view__footer-banner application-view__footer-banner--neutral" onClick={e => e.stopPropagation()}>
                                                <span>Processing...</span>
                                                <Spinner size="sm" className='me-2'> Loading... </Spinner>
                                            </div>
                                        ) : null}

                                        {applicationStatus === 'rejected' ? (
                                            <button
                                                type="button"
                                                className="application-view__footer-btn application-view__footer-revert-btn"
                                                disabled={!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)}
                                                onClick={revertRejection}
                                            >
                                                <span className="application-view__footer-revert-btn__label application-view__footer-revert-btn__label--default">
                                                    REJECTED
                                                </span>
                                                <span className="application-view__footer-revert-btn__label application-view__footer-revert-btn__label--hover">
                                                    REVERT REJECTION
                                                </span>
                                            </button>
                                        ) : null}

                                        {applicationStatus === 'accepted' ? (
                                            <button type="button" className="application-view__footer-status application-view__footer-status--approved" disabled>
                                                Approved
                                            </button>
                                        ) : null}
                                    </div>
                                </div>
                            )}
                    </div>
                    {hasDemographicInsights && !shouldMergePanels ? (
                        <div className="application-view__panel pb-3"> {/* Panel 2 */}
                            <PerfectScrollbar options={{ suppressScrollX: true }}>
                                <div className="application-view__panel-content">
                                    {socialAccount && (
                                        <SocialAccountInsights
                                            separatePanel={showSeparatePanelForDemographicInsights}
                                            socialAccount={socialAccount}
                                        />
                                    )}
                                </div>
                            </PerfectScrollbar>
                        </div>
                    ) : null}
                    <div className="application-view__panel pb-3"> {/* Panel 3 */}
                        <PerfectScrollbar options={{ suppressScrollX: true }}>
                            <div className="application-view__panel-content">
                                {socialAccount && socialAccountId && (
                                    <ApplicationViewMedia
                                        influencer={influencer}
                                        socialAccountId={socialAccountId}
                                        socialAccount={socialAccount}
                                    />
                                )}
                            </div>
                        </PerfectScrollbar>
                    </div>
                </div>
            </OffcanvasBody>
        </Offcanvas>
    )
}

export default ApplicationView